/**
 * Store Rows
 *
 * Dense, sortable list view of every store. The card grid is the right shape for
 * a handful of stores; at 40+ it becomes a wall you scroll rather than read, so
 * this is the default view and the cards stay behind a toggle.
 *
 * Default order is "what needs attention": packing backlog, then stores that
 * aren't trading. Any column header re-sorts.
 *
 * @module StoreCommandCenter/components
 */

import React, { useMemo, useState } from 'react';
import { Box, Typography, TextField, InputAdornment } from '@mui/material';
import { Search as SearchIcon } from '@mui/icons-material';

import type { StoreCommandData } from '../hooks/useCommandCenter';
import { formatMinutes } from '../hooks/useCommandCenter';
import { useAdminTheme, type AdminColorTokens } from '../../../admin-theme';

// ============================================================================
// Name splitting
// ============================================================================

/**
 * Nearly every store is named "<Operator> <Branch>" — 43 of 44 begin with
 * "Vaperite". Rendering the whole string in a fixed-width cell truncates the
 * branch, which is the only part that identifies it. Split the operator off so
 * the distinguishing words survive.
 */
export function splitStoreName(name: string): { group: string | null; branch: string } {
  const trimmed = (name ?? '').trim();

  // "DEELIVER - Diep River" / "Deeliver — Diep River"
  const dashed = trimmed.match(/^(deeliver)\s*[-—–]\s*(.+)$/i);
  if (dashed) return { group: 'Deeliver', branch: dashed[2].trim() };

  const prefixed = trimmed.match(/^(vaperite|deeliver)\s+(.+)$/i);
  if (prefixed) {
    const group = prefixed[1].toLowerCase() === 'vaperite' ? 'Vaperite' : 'Deeliver';
    return { group, branch: prefixed[2].trim() };
  }

  return { group: null, branch: trimmed };
}

// ============================================================================
// Sorting
// ============================================================================

type SortKey = 'attention' | 'name' | 'packing' | 'inTransit' | 'awaitingPickup' | 'fulfilment';

/**
 * Rank for the default view: packing backlog, then a store that is active but
 * not currently trading.
 */
function attentionScore(store: StoreCommandData): number {
  const packing = store.signals?.packing ?? 0;
  const notTrading = store.isActive && !store.isOpen ? 1 : 0;
  return packing * 10 + notTrading;
}

// ============================================================================
// Cells
// ============================================================================

interface CountCellProps {
  value: number;
  /** At or above this, the number is rendered as critical. */
  hotAt?: number;
  colors: AdminColorTokens;
}

/** Zero renders as a dim dash so a non-zero number is what the eye catches. */
function CountCell({ value, hotAt, colors }: CountCellProps): React.ReactElement {
  if (!value) {
    return (
      <Box component="td" sx={{ ...cellSx, textAlign: 'right', color: colors.textMuted }}>
        –
      </Box>
    );
  }
  const color = hotAt && value >= hotAt ? colors.errorGlow : value >= 3 ? colors.warning : colors.textPrimary;
  return (
    <Box component="td" sx={{ ...cellSx, textAlign: 'right', color, fontWeight: value >= 3 ? 700 : 500 }}>
      {value}
    </Box>
  );
}

const cellSx = {
  px: 1.5,
  py: 1.1,
  whiteSpace: 'nowrap',
  fontVariantNumeric: 'tabular-nums',
  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
  fontSize: '0.82rem',
} as const;

// ============================================================================
// Component
// ============================================================================

export interface StoreRowsProps {
  stores: StoreCommandData[];
  selectedStoreId: string | null;
  onSelect: (storeId: string) => void;
}

export default function StoreRows({ stores, selectedStoreId, onSelect }: StoreRowsProps): React.ReactElement {
  const { colors } = useAdminTheme();
  const [sortKey, setSortKey] = useState<SortKey>('attention');
  const [query, setQuery] = useState('');

  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const filtered = q ? stores.filter((s) => s.name.toLowerCase().includes(q)) : stores;

    return [...filtered].sort((a, b) => {
      switch (sortKey) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'fulfilment':
          return (b.performance?.avgFulfillmentMinutes ?? -1) - (a.performance?.avgFulfillmentMinutes ?? -1);
        case 'packing':
          return (b.signals?.packing ?? 0) - (a.signals?.packing ?? 0);
        case 'inTransit':
          return (b.signals?.inTransit ?? 0) - (a.signals?.inTransit ?? 0);
        case 'awaitingPickup':
          return (b.signals?.awaitingPickup ?? 0) - (a.signals?.awaitingPickup ?? 0);
        default:
          return attentionScore(b) - attentionScore(a);
      }
    });
  }, [stores, sortKey, query]);

  const headers: Array<{ key: SortKey; label: string; numeric: boolean }> = [
    { key: 'name', label: 'Store', numeric: false },
    { key: 'packing', label: 'Packing', numeric: true },
    { key: 'inTransit', label: 'Transit', numeric: true },
    { key: 'awaitingPickup', label: 'Pickup', numeric: true },
    { key: 'fulfilment', label: 'Avg fulfil', numeric: true },
  ];

  return (
    <Box>
      {/* Search beats status chips once there are more stores than fit on a screen. */}
      <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <TextField
          size="small"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Find a store…"
          aria-label="Find a store"
          sx={{
            minWidth: 240,
            '& .MuiOutlinedInput-root': {
              bgcolor: colors.bgCard,
              color: colors.textPrimary,
              '& fieldset': { borderColor: colors.border },
              '&:hover fieldset': { borderColor: colors.borderGlow },
            },
          }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon sx={{ fontSize: 18, color: colors.textMuted }} />
              </InputAdornment>
            ),
          }}
        />
        <Typography variant="caption" sx={{ color: colors.textMuted }}>
          {rows.length} of {stores.length} stores
          {sortKey === 'attention' && ' · sorted by what needs attention'}
        </Typography>
      </Box>

      <Box sx={{ overflowX: 'auto', border: `1px solid ${colors.border}`, borderRadius: 2, bgcolor: colors.bgCard }}>
        <Box component="table" sx={{ width: '100%', minWidth: 720, borderCollapse: 'collapse' }}>
          <Box component="thead">
            <Box component="tr">
              {headers.map((h) => (
                <Box
                  component="th"
                  key={h.key}
                  onClick={() => setSortKey(h.key)}
                  aria-sort={sortKey === h.key ? 'descending' : 'none'}
                  sx={{
                    px: 1.5,
                    py: 1.2,
                    textAlign: h.numeric ? 'right' : 'left',
                    fontSize: '0.66rem',
                    fontWeight: 700,
                    letterSpacing: '0.07em',
                    textTransform: 'uppercase',
                    color: sortKey === h.key ? colors.info : colors.textMuted,
                    borderBottom: `1px solid ${colors.border}`,
                    cursor: 'pointer',
                    whiteSpace: 'nowrap',
                    userSelect: 'none',
                    '&:hover': { color: colors.textSecondary },
                  }}
                >
                  {h.label}
                </Box>
              ))}
              <Box
                component="th"
                sx={{
                  px: 1.5,
                  py: 1.2,
                  textAlign: 'left',
                  fontSize: '0.66rem',
                  fontWeight: 700,
                  letterSpacing: '0.07em',
                  textTransform: 'uppercase',
                  color: colors.textMuted,
                  borderBottom: `1px solid ${colors.border}`,
                  whiteSpace: 'nowrap',
                }}
              >
                Status
              </Box>
            </Box>
          </Box>

          <Box component="tbody">
            {rows.map((store) => {
              const { group, branch } = splitStoreName(store.name);
              const selected = store.id === selectedStoreId;
              const paused = store.isPaused;
              const comingSoon = !store.isActive;

              return (
                <Box
                  component="tr"
                  key={store.id}
                  onClick={() => onSelect(store.id)}
                  sx={{
                    cursor: 'pointer',
                    borderBottom: `1px solid ${colors.border}`,
                    bgcolor: selected ? colors.bgElevated : 'transparent',
                    boxShadow: selected ? `inset 3px 0 0 ${colors.info}` : 'none',
                    '&:hover': { bgcolor: colors.bgElevated },
                  }}
                >
                  <Box component="td" sx={{ ...cellSx, fontFamily: 'inherit', fontSize: '0.85rem' }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          flexShrink: 0,
                          bgcolor: paused
                            ? colors.warning
                            : comingSoon
                              ? colors.textMuted
                              : store.isOpen
                                ? colors.successGlow
                                : colors.errorGlow,
                        }}
                      />
                      {group && (
                        <Box
                          component="span"
                          sx={{
                            fontSize: '0.6rem',
                            fontWeight: 700,
                            letterSpacing: '0.05em',
                            textTransform: 'uppercase',
                            color: group === 'Deeliver' ? colors.info : colors.textMuted,
                            border: `1px solid ${group === 'Deeliver' ? colors.info : colors.border}`,
                            borderRadius: '4px',
                            px: 0.6,
                            py: '1px',
                            flexShrink: 0,
                          }}
                        >
                          {group}
                        </Box>
                      )}
                      <Box
                        component="span"
                        sx={{
                          color: colors.textPrimary,
                          fontWeight: 550,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {branch}
                      </Box>
                    </Box>
                  </Box>

                  <CountCell value={store.signals?.packing ?? 0} hotAt={5} colors={colors} />
                  <CountCell value={store.signals?.inTransit ?? 0} colors={colors} />
                  <CountCell value={store.signals?.awaitingPickup ?? 0} colors={colors} />

                  <Box
                    component="td"
                    sx={{
                      ...cellSx,
                      textAlign: 'right',
                      color: store.performance?.avgFulfillmentMinutes ? colors.textPrimary : colors.textMuted,
                    }}
                  >
                    {formatMinutes(store.performance?.avgFulfillmentMinutes ?? null)}
                  </Box>

                  <Box
                    component="td"
                    sx={{
                      ...cellSx,
                      fontFamily: 'inherit',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      color: paused ? colors.warning : comingSoon ? colors.textMuted : store.isOpen ? colors.successGlow : colors.errorGlow,
                    }}
                  >
                    {paused ? 'Paused' : comingSoon ? 'Coming soon' : store.isOpen ? 'Open' : 'Closed'}
                    {store.isPastCutoff && !paused && (
                      <Box component="span" sx={{ color: colors.textMuted, ml: 0.75, fontWeight: 500 }}>
                        past cutoff
                      </Box>
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>

      {rows.length === 0 && (
        <Box sx={{ py: 5, textAlign: 'center' }}>
          <Typography variant="body2" sx={{ color: colors.textSecondary }}>
            No store matches “{query}”.
          </Typography>
        </Box>
      )}
    </Box>
  );
}
